"use client"

import { createContext, useContext, useState, useEffect, ReactNode, useCallback, useMemo } from "react"
import type { LoginResponse } from "@/sdk/shared/auth/types"
import { isJwtExpired, getCookie as getSharedCookie, deleteCookie as deleteSharedCookie, setCookie as setSharedCookie } from "@/sdk/shared/auth/utils"

interface AuthUser {
  id: string
  email: string
  fullName?: string
  firstName?: string
  lastName?: string
  first_name?: string
  last_name?: string
  dateOfBirth?: string | null
  dob?: string | null
  date_of_birth?: string | null
  city?: string | null
  state?: string | null
  country?: string | null
  termsAndConditionsAccepted?: boolean
  terms_and_conditions_accepted?: boolean
  termsAndConditions?: boolean
  accessGate?: any
  role: string
  roleSlug?: string | null
  status?: string
  emailVerified?: boolean
  photoUrl?: string | null
  photo_url?: string | null
  tenant: {
    id: string
    name: string
    slug: string
    status?: string
    plan?: string
  }
  features?: Array<{
    slug: string
    name: string
    access_key: string
    access_level: number
  }> | null
}

interface AuthContextType {
  user: AuthUser | null
  userType: 'user' | 'client' | 'ib' | null
  roleSlug: string | null
  features: AuthUser['features']
  isAuthenticated: boolean
  isLoading: boolean
  setAuth: (response: LoginResponse) => void
  updateUser: (fields: Partial<AuthUser>) => void
  clearAuth: () => void
  hasFeatureAccess: (featureSlug: string, minAccessLevel?: number) => boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

const getCookie = getSharedCookie
const setCookie = setSharedCookie
const deleteCookie = deleteSharedCookie

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [userType, setUserType] = useState<'user' | 'client' | 'ib' | null>(null)
  const [roleSlug, setRoleSlug] = useState<string | null>(null)
  const [features, setFeatures] = useState<AuthUser['features']>(null)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  const clearAuth = useCallback(() => {
    setUser(null)
    setUserType(null)
    setRoleSlug(null)
    setFeatures(null)
    setIsAuthenticated(false)

    if (typeof window !== 'undefined') {
      try {
        for (let i = sessionStorage.length - 1; i >= 0; i--) {
          const key = sessionStorage.key(i)
          if (key && key.startsWith('crm_ai_chat_')) {
            sessionStorage.removeItem(key)
          }
        }
      } catch {}
    }

    const isAdminRoute = typeof window !== 'undefined' && window.location.pathname.startsWith('/admin')

    if (typeof window !== 'undefined' && sessionStorage.getItem('force_auth_token') && !isAdminRoute) {
      // Impersonated session clear: only clear force auth from session storage and gate cookies!
      sessionStorage.removeItem('force_auth_token')
      sessionStorage.removeItem('force_auth_user')
      sessionStorage.removeItem('force_auth_user_type')
      sessionStorage.removeItem('force_auth_session_id')
      sessionStorage.removeItem('force_auth_admin')
      sessionStorage.removeItem('force_auth_started_at')
      deleteCookie('force_auth')
      deleteCookie('client_access_gate')
      return
    }

    deleteCookie('auth_user')
    deleteCookie('auth_user_type')
    deleteCookie('auth_token')
    deleteCookie('client_role')
    deleteCookie('client_access_gate')
    deleteCookie('force_auth')
  }, [])

  // Load user data from sessionStorage (force-login, tab-specific) or cookies (normal login)
  useEffect(() => {
    const loadAuth = async () => {
      const pathname = typeof window !== 'undefined' ? window.location.pathname : ''
      const isAdminRoute = pathname.startsWith('/admin')

      // If viewing an admin route, purge any lingering client force-auth state from sessionStorage immediately
      if (isAdminRoute && typeof window !== 'undefined') {
        sessionStorage.removeItem('force_auth_token')
        sessionStorage.removeItem('force_auth_user')
        sessionStorage.removeItem('force_auth_user_type')
        sessionStorage.removeItem('force_auth_session_id')
        sessionStorage.removeItem('force_auth_admin')
        sessionStorage.removeItem('force_auth_started_at')
        deleteCookie('force_auth')
        deleteCookie('client_access_gate')
      }

      // Check sessionStorage ONLY for non-admin client impersonation sessions
      if (typeof window !== 'undefined' && !isAdminRoute) {
        const forceUser = sessionStorage.getItem('force_auth_user')
        const forceType = sessionStorage.getItem('force_auth_user_type')
        if (forceUser && forceType) {
          try {
            const parsedUser = JSON.parse(forceUser)
            setUser(parsedUser)
            setUserType(forceType as 'user' | 'client' | 'ib')
            const userRoleSlug = parsedUser.roleSlug || (typeof parsedUser.role === 'string' ? parsedUser.role : null) || null
            setRoleSlug(userRoleSlug)
            setFeatures(parsedUser.features || [])
            setIsAuthenticated(true)
            setIsLoading(false)
            return
          } catch (error) {
            console.error('Failed to parse force-login user data:', error)
          }
        }
      }

      // Fall back to cookies (normal login flow or restored admin session)
      let storedToken = getCookie('auth_token')
      let storedUser = getCookie('auth_user')
      let storedUserType = getCookie('auth_user_type')

      // Self-healing for admin routes: if on an admin route and cookies are missing or client-typed,
      // attempt recovery from admin_session_backup
      if (isAdminRoute && (!storedToken || storedUserType === 'client' || isJwtExpired(storedToken))) {
        try {
          const backupStr = localStorage.getItem('admin_session_backup') || sessionStorage.getItem('admin_session_backup')
          if (backupStr) {
            const backup = JSON.parse(backupStr)
            if (backup.token && !isJwtExpired(backup.token) && backup.userType !== 'client') {
              setCookie('auth_token', backup.token, 7)
              if (backup.user) {
                const userVal = typeof backup.user === 'string' ? backup.user : encodeURIComponent(JSON.stringify(backup.user))
                setCookie('auth_user', userVal, 7)
                storedUser = userVal
              }
              if (backup.userType) {
                setCookie('auth_user_type', backup.userType, 7)
                storedUserType = backup.userType
              }
              if (backup.role) setCookie('client_role', backup.role, 7)
              if (backup.tenantSlug) setCookie('tenant_slug', backup.tenantSlug, 7)
              if (backup.tenantId) setCookie('tenant_id', backup.tenantId, 7)
              storedToken = backup.token
            }
          }
        } catch (e) {
          console.warn('[AuthContext] Admin session recovery failed:', e)
        }
      }

      const hasValidToken = Boolean(storedToken && !isJwtExpired(storedToken))

      if (hasValidToken && storedUser && storedUserType) {
        try {
          const parsedUser = JSON.parse(decodeURIComponent(storedUser))
          setUser(parsedUser)
          setUserType(storedUserType as 'user' | 'client' | 'ib')
          const userRoleSlug = parsedUser.roleSlug || (typeof parsedUser.role === 'string' ? parsedUser.role : null) || getCookie('client_role') || null
          setRoleSlug(userRoleSlug)
          setFeatures(parsedUser.features || [])
          setIsAuthenticated(true)
        } catch (error) {
          console.error('Failed to parse stored user data:', error)
          clearAuth()
        }
      } else {
        if (storedUser || storedUserType || storedToken) {
          clearAuth()
        }
      }
      setIsLoading(false)
    }

    loadAuth()
  }, [clearAuth])

  const setAuth = useCallback((response: LoginResponse) => {
    const authUser = response.userType === 'client' ? response.client : response.user
    if (authUser && response.userType) {
      setUser(authUser)
      setUserType(response.userType)
      setIsAuthenticated(true)

      const isAdminRoute = typeof window !== 'undefined' && window.location.pathname.startsWith('/admin')

      // Sync sessionStorage if force-login session is active (client portal only)
      if (typeof window !== 'undefined' && !isAdminRoute) {
        const hasForceSession = sessionStorage.getItem('force_auth_token') || sessionStorage.getItem('force_auth_user')
        if (hasForceSession) {
          sessionStorage.setItem('force_auth_user', JSON.stringify(authUser))
          sessionStorage.setItem('force_auth_user_type', response.userType)
          if (response.token) {
            sessionStorage.setItem('force_auth_token', response.token)
          }
          return
        }
      }

      // Set features and roleSlug from login response
      if (response.userType === 'user' && response.user) {
        const userRoleSlug = response.user.roleSlug || (typeof response.user.role === 'string' ? response.user.role : null) || null
        setRoleSlug(userRoleSlug)
        setFeatures(response.user.features || [])
      }

      setCookie('auth_user', encodeURIComponent(JSON.stringify(authUser)))
      setCookie('auth_user_type', response.userType)
      if (authUser.tenant?.slug) {
        setCookie('tenant_slug', authUser.tenant.slug)
      }
      if (authUser.tenant?.id) {
        setCookie('tenant_id', authUser.tenant.id)
      }
    }
  }, [])

  const updateUser = useCallback((fields: Partial<AuthUser>) => {
    setUser((prev) => {
      if (!prev) return null
      const updated = { ...prev, ...fields }
      const isAdminRoute = typeof window !== 'undefined' && window.location.pathname.startsWith('/admin')
      if (typeof window !== 'undefined' && !isAdminRoute) {
        const forceUser = sessionStorage.getItem('force_auth_user')
        if (forceUser) {
          sessionStorage.setItem('force_auth_user', JSON.stringify(updated))
          return updated
        }
      }
      setCookie('auth_user', encodeURIComponent(JSON.stringify(updated)))
      return updated
    })
  }, [])

  const hasFeatureAccess = useCallback(
    (featureSlug: string, minAccessLevel: number = 10): boolean => {
      if (roleSlug === 'admin' || roleSlug === 'super_admin') return true
      if (!features || !Array.isArray(features)) return false
      const feat = features.find((f) => f.slug === featureSlug)
      if (!feat) return false
      return feat.access_level >= minAccessLevel
    },
    [roleSlug, features]
  )

  const value = useMemo(
    () => ({
      user,
      userType,
      roleSlug,
      features,
      isAuthenticated,
      isLoading,
      setAuth,
      updateUser,
      clearAuth,
      hasFeatureAccess,
    }),
    [user, userType, roleSlug, features, isAuthenticated, isLoading, setAuth, updateUser, clearAuth, hasFeatureAccess]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}
